import { Flex, Grid, Typography } from 'antd';
import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  AUTH_BANNER_GAP,
  AUTH_BANNER_PADDING,
  AUTH_TITLE_MAX_WIDTH,
  authPanelMotionStyle,
} from '@/components/auth/authLayout';
import { HOME, PRIVACY_POLICY } from '@/constants/routes';
import LogoImage from '@/icons/logo.svg?react';
import { SPACE_XL } from '@/theme/buildAntdTheme';
import Loader from '@/UI/Loader';

import AuthMarketingLottieBoundary from './AuthMarketingLottieBoundary';
import LanguageSwitcher from './LanguageSwitcher';

const AuthMarketingLottie = lazy(() => import('./AuthMarketingLottie'));

interface AuthMarketingBannerProps {
  title: string;
  clickable?: boolean;
  entered?: boolean;
  prefersReducedMotion?: boolean;
}

const AuthMarketingBanner = ({
  title,
  clickable = true,
  entered = true,
  prefersReducedMotion = true,
}: AuthMarketingBannerProps) => {
  const [t] = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  return (
    <Flex
      vertical
      gap={AUTH_BANNER_GAP}
      align="flex-start"
      justify="space-between"
      style={{
        padding: AUTH_BANNER_PADDING,
        position: 'relative',
        height: '100%',
        transition: prefersReducedMotion ? undefined : 'width 0.3s ease',
        background: 'var(--ant-color-text-heading)',
        ...authPanelMotionStyle(entered, prefersReducedMotion),
      }}
    >
      <Flex
        vertical
        gap={SPACE_XL}
        style={{ position: 'relative', width: '100%', zIndex: 2 }}
      >
        <Flex component="nav" justify="space-between" align="center">
          {clickable ? (
            <Link to={HOME} aria-label={t('HOME')}>
              <LogoImage />
            </Link>
          ) : (
            <LogoImage />
          )}
          <LanguageSwitcher />
        </Flex>

        <Flex style={{ maxWidth: AUTH_TITLE_MAX_WIDTH }}>
          <Typography.Title level={1} type="success">
            {title}
          </Typography.Title>
        </Flex>
      </Flex>

      {!isMobile && (
        <div style={{ width: '70%', margin: '0 auto' }}>
          <AuthMarketingLottieBoundary>
            <Suspense
              fallback={<Loader centered size="large" minHeight={240} />}
            >
              <AuthMarketingLottie />
            </Suspense>
          </AuthMarketingLottieBoundary>
        </div>
      )}
      <Link to={PRIVACY_POLICY} style={{ position: 'relative', zIndex: 2 }}>
        <Typography.Text type="secondary">
          {t('PRIVACY_POLICY')}
        </Typography.Text>
      </Link>
    </Flex>
  );
};

export default AuthMarketingBanner;
